import React, { useMemo, useState } from 'react';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { PageLayout } from '../../shared/PageLayout';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { StatCard } from '../../shared/StatCard';
import { Avatar } from '../shared/Avatar';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { getModuleLabel } from '../shared/moduleMetadata';
import { getMasteryBand } from '../../shared/masteryBands';
import { formatRelativeTime, formatDateWithTime } from '../../../utils/formatters';
import { downloadCsv } from '../../../utils/csvExport';
import { TablePagination } from '../shared/TablePagination';
// Pseudoniem + stabiele hash komen uit één gedeelde bron, zodat hetzelfde kind
// hier hetzelfde label krijgt als in de accountlijst. Deze twee functies stonden
// tot 12 aug 2026 lokaal in dit bestand, met een ander algoritme dan de server —
// zie de toelichting in learnerPseudonym.ts.
import { pseudonymFor, stableHash } from '../../shared/learnerPseudonym';
import { Activity, Filter, Search, Clock, BookOpen, TrendingUp, Download, ShieldAlert } from 'lucide-react';
import { AdminTabs } from '../shared/AdminTabs';
import { AccessLogPanel } from './logs/AccessLogPanel';
type LogTab = 'sessions' | 'access';
interface SessionAnswer {
  isCorrect: boolean;
}
interface SessionLog {
  id: string;
  accountId: string;
  subjectId: string;
  startTime: number;
  answers: SessionAnswer[];
}
export function LogsPage() {
  const {
    accounts,
    getLearnerData
  } = useAppStore();
  const allAccounts = useMemo(() => accounts?.accountsById ? Object.values(accounts.accountsById) : [], [accounts]);
  // `now` captured once per render so deterministic fallback timestamps
  // remain stable within a single render pass.
  const now = Date.now();
  const sessions = useMemo(() => {
    const allSessions: SessionLog[] = [];
    allAccounts.filter(a => a.role === 'learner').forEach(learner => {
      const data = getLearnerData(learner.id);
      if (!data?.familyMastery) return;
      Object.values(data.familyMastery).forEach(mastery => {
        if (mastery.attempts > 0) {
          const subjectId = mastery.familyId.split('-')[0];
          // Deterministic offset within last 7 days when no real lastSeen.
          // Same (learner, family) pair → same pseudo-time on every render.
          const fallbackOffset = stableHash(`${learner.id}:${mastery.familyId}`) % (7 * 86400000);
          allSessions.push({
            id: `sess-${learner.id}-${mastery.familyId}`,
            accountId: learner.id,
            subjectId: subjectId || 'UNK',
            startTime: mastery.lastSeen ?? now - fallbackOffset,
            answers: Array.from({
              length: Math.min(10, mastery.attempts)
            }).map((_, i) => ({
              isCorrect: i < mastery.correct / mastery.attempts * Math.min(10, mastery.attempts)
            }))
          });
        }
      });
    });
    return allSessions.sort((a, b) => b.startTime - a.startTime);
  }, [allAccounts, getLearnerData, now]);
  const [activeTab, setActiveTab] = useState<LogTab>('sessions');
  const [search, setSearch] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | '1d' | '7d' | '30d'>('7d');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const dateThreshold = useMemo(() => {
    const now = Date.now();
    if (dateFilter === '1d') return now - 86400000;
    if (dateFilter === '7d') return now - 7 * 86400000;
    if (dateFilter === '30d') return now - 30 * 86400000;
    return 0;
  }, [dateFilter]);
  const allSubjects = useMemo(() => Array.from(new Set(sessions.map(s => s.subjectId).filter(Boolean))).sort(), [sessions]);
  const filteredSessions = useMemo(() => {
    return sessions.filter(s => {
      const matchDate = s.startTime >= dateThreshold;
      const matchSubject = !subjectFilter || s.subjectId === subjectFilter;
      // Zoeken op pseudoniem, niet op naam. Bleef zoeken-op-naam bestaan,
      // dan is de naam alsnog af te leiden uit welke rijen oplichten — en
      // dan is het maskeren op het scherm alleen decoratie.
      const matchSearch = !search || pseudonymFor(s.accountId).toLowerCase().includes(search.toLowerCase()) || s.subjectId?.toLowerCase().includes(search.toLowerCase()) || s.id?.toLowerCase().includes(search.toLowerCase());
      return matchDate && matchSubject && matchSearch;
    }).sort((a, b) => b.startTime - a.startTime);
  }, [sessions, allAccounts, dateThreshold, subjectFilter, search]);
  const paginatedSessions = useMemo(() => filteredSessions.slice(page * pageSize, (page + 1) * pageSize), [filteredSessions, page, pageSize]);
  const stats = useMemo(() => {
    const now = Date.now();
    const today = sessions.filter(s => s.startTime >= now - 86400000);
    const week = sessions.filter(s => s.startTime >= now - 7 * 86400000);
    const avgScore = filteredSessions.length > 0 ? Math.round(filteredSessions.reduce((sum, s) => {
      const correct = s.answers?.filter(a => a.isCorrect).length ?? 0;
      const total = s.answers?.length ?? 0;
      return sum + (total > 0 ? correct / total * 100 : 0);
    }, 0) / filteredSessions.length) : 0;
    return {
      today: today.length,
      week: week.length,
      total: sessions.length,
      avgScore
    };
  }, [sessions, filteredSessions]);
  const exportCsv = () => {
    // GEEN leerlingnamen in de export.
    //
    // Dit scherm bestaat om te zien of het systeem gebruikt wordt en of de
    // scores plausibel zijn — een aggregatievraag. Daar is geen naam voor
    // nodig. De oude export zette naam + score per sessie in een los
    // CSV-bestand dat daarna buiten elke toegangscontrole om rondgaat: geen
    // RLS, geen intrekbaarheid, geen spoor van wie het opende.
    //
    // Het pseudoniem is stabiel, dus twee regels van hetzelfde kind zijn nog
    // steeds als één kind te herkennen — precies wat je voor analyse nodig
    // hebt. Wie tóch moet weten wélk kind het is, hoort dat via het
    // gebruikersscherm met een gelogd toegangsvenster te doen.
    const rows = [['ID', 'Leerling (pseudoniem)', 'Vak', 'Score %', 'Vragen', 'Datum'], ...filteredSessions.map(s => {
      const correct = s.answers?.filter(a => a.isCorrect).length ?? 0;
      const total = s.answers?.length ?? 0;
      const pct = total > 0 ? Math.round(correct / total * 100) : 0;
      return [s.id, pseudonymFor(s.accountId), s.subjectId, pct, total, formatDateWithTime(s.startTime)];
    })];
    const csv = rows.map(r => r.join(',')).join('\n');
    downloadCsv(csv, 'sessies');
  };
  return <PageLayout title="Activiteitenlog" subtitle={activeTab === 'access' ? 'Wie heeft leerlinggegevens ingezien, en waarom' : 'Overzicht van alle oefensessies'} actions={activeTab === 'sessions' ? <div className="flex items-center gap-2" data-id="element-7242" data-mp="2sd6s4y2adnsm">
          {/*
                 Sessions are derived from real `familyMastery` aggregates but the
                 per-answer breakdown and fallback timestamps are reconstructed
                 client-side. Surface that honestly to admins (audit §19.2.8 / V15).
               */}
          <ConnectionBadge status="mock" label="Demo-sessies" data-id="element-7243" data-mp="lnburvq9rd6a" />
          <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors bg-white shadow-sm" data-id="element-7244" data-mp="lnvcpcfx61kb">
          
            <Download className="w-4 h-4" data-id="element-7245" data-mp="3p5wqe282i6ie" /> Export CSV
          </button>
        </div> :
  /*
   * Bewust GEEN CSV-export op het inzage-logboek. Een auditspoor dat als
   * los bestand naar buiten gaat, valt daarna buiten elke toegangscontrole
   * en is niet meer in te trekken — en dit logboek bestaat juist om
   * ongecontroleerde inzage tegen te gaan.
   */
  <ConnectionBadge status="live" data-id="element-7246" data-mp="1dfaw6xm6j86b" />} breadcrumbs={[{
    label: 'Systeem'
  }, {
    label: 'Activiteitenlog'
  }]} data-id="element-7241" data-mp="1ztgopz419en8">
      
      {/*
        Twee logboeken op één pagina, met tabs in plaats van gestapeld. Ze
        beantwoorden verschillende vragen — "wordt er geoefend" versus "wie heeft
        er gekeken" — en gestapeld zou het tweede altijd onder een lange tabel
        eindigen. Juist het inzage-logboek moet vindbaar zijn.
       */}
      <AdminTabs tabs={[{
      id: 'sessions',
      label: 'Oefensessies',
      icon: <Activity className="w-4 h-4" data-id="element-7248" data-mp="2g6ltef16j8jd" />
    }, {
      id: 'access',
      label: 'Inzage in leerlinggegevens',
      icon: <ShieldAlert className="w-4 h-4" data-id="element-7249" data-mp="2l8b8q6vfq8d9" />
    }]} activeTab={activeTab} onTabChange={id => setActiveTab(id as LogTab)} data-id="element-7247" data-mp="2ba56fo5pv07v" />

      {activeTab === 'access' && <AccessLogPanel data-id="element-7250" data-mp="2u76ddeitzaeg" />}

      {activeTab === 'sessions' && <>
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-7251" data-mp="vrjw6srz9gws">
        <StatCard label="Vandaag" value={stats.today} icon={<Clock className="w-5 h-5" data-id="element-7253" data-mp="ztwzlaj952qe" />} color="blue" data-id="element-7252" data-mp="vtmx47uo4uga" />
        
        <StatCard label="Deze week" value={stats.week} icon={<Activity className="w-5 h-5" data-id="element-7255" data-mp="saxgz73m7f83" />} color="emerald" data-id="element-7254" data-mp="s92wftw99p4j" />
        
        <StatCard label="Totaal sessies" value={stats.total} icon={<BookOpen className="w-5 h-5" data-id="element-7257" data-mp="ngtasq26bb82" />} color="purple" data-id="element-7256" data-mp="nie3sb8uam3t" />
        
        <StatCard label="Gem. score" value={`${stats.avgScore}%`} icon={<TrendingUp className="w-5 h-5" data-id="element-7259" data-mp="eihkyq94shgo" />} color="amber" data-id="element-7258" data-mp="ik2uq2qdcfmn" />
        
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4 bg-white p-3 sm:p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-7260" data-mp="1b8guoo10pm0u">
        <div className="flex-1 w-full sm:max-w-sm" data-id="element-7261" data-mp="16afb81zojkou">
          <Input type="text" placeholder="Zoek op pseudoniem of vak..." value={search} onChange={e => {
            setSearch(e.target.value);
            setPage(0);
          }} icon={<Search className="w-4 h-4" data-id="element-7263" data-mp="xajw5m3075jj" />} size="sm" data-id="element-7262" data-mp="127ab2632nv3d" />
          
        </div>

        <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar pb-1 sm:pb-0" data-id="element-7264" data-mp="3mzcfnifryvjo">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-shrink-0" data-id="element-7265" data-mp="3hxvi9wwi1r7u">
            {(['all', '1d', '7d', '30d'] as const).map(d => <button key={d} onClick={() => {
              setDateFilter(d);
              setPage(0);
            }} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${dateFilter === d ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-7266" data-mp="enqxuazi2heg">
              
                {d === 'all' ? 'Alles' : d === '1d' ? '24u' : d === '7d' ? '7d' : '30d'}
              </button>)}
          </div>

          <div className="flex-shrink-0" data-id="element-7267" data-mp="xerxz2ccwizl">
            <Select value={subjectFilter} onChange={e => {
              setSubjectFilter(e.target.value);
              setPage(0);
            }} options={allSubjects.map(s => ({
              value: s,
              label: getModuleLabel(s) || s
            }))} placeholder="Alle vakken" icon={<Filter className="w-3.5 h-3.5" data-id="element-7269" data-mp="3d6foog0s1f9y" />} fullWidth={false} size="sm" data-id="element-7268" data-mp="xe0akeswxrke" />
            
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-7270" data-mp="3965hj0190iti">
        <div className="overflow-x-auto" data-id="element-7271" data-mp="2z28ej8ykazwv">
          <table className="w-full text-sm text-left" data-id="element-7272" data-mp="331pmqa5dib5q">
            <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 font-medium" data-id="element-7273" data-mp="333addookh9yb">
              <tr data-id="element-7274" data-mp="2p88c5euo08ru">
                <th className="px-4 py-3" data-id="element-7275" data-mp="2u8w0ttvf8diw">Sessie ID</th>
                <th className="px-4 py-3" data-id="element-7276" data-mp="2u7kupnei6tom">Leerling</th>
                <th className="px-4 py-3" data-id="element-7277" data-mp="2fbde0x2b0ae3">Vak</th>
                <th className="px-4 py-3" data-id="element-7278" data-mp="2fak5bqjsbatb">Score</th>
                <th className="px-4 py-3" data-id="element-7279" data-mp="2kbhnw48pi6vc">Tijdstip</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50" data-id="element-7280" data-mp="26bqbg7xu8yjg">
              {paginatedSessions.length === 0 ? <tr data-id="element-7281" data-mp="2ba06mijkbz52">
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-400" data-id="element-7282" data-mp="2b9i5p8lygwqt">
                  
                    Geen sessies gevonden
                  </td>
                </tr> : paginatedSessions.map(session => {
                const correct = session.answers?.filter(a => a.isCorrect).length ?? 0;
                const total = session.answers?.length ?? 0;
                const pct = total > 0 ? Math.round(correct / total * 100) : 0;
                return <tr key={session.id} className="hover:bg-gray-50 transition-colors" data-id="element-7283" data-mp="34r9byxf7d8m">
                    
                      <td className="px-4 py-3 font-mono text-xs text-gray-500" data-id="element-7284" data-mp="3p9uypkx62uo0">
                        {session.id.split('-').slice(0, 2).join('-')}...
                      </td>
                      <td className="px-4 py-3" data-id="element-7285" data-mp="qubvsrss8smn">
                        <div className="flex items-center gap-2" data-id="element-7286" data-mp="lsw9kwevcg2o">
                          {/*
                           Pseudoniem in plaats van de naam. Dit scherm meet
                           of het systeem gebruikt wordt; daarvoor is geen
                           naam nodig, en een lijst met kindernamen op het
                           scherm van een beheerder is zelf een verwerking.
                           */}
                          <Avatar name={pseudonymFor(session.accountId)} data-id="element-7287" data-mp="14qfouhrx6uxj" />
                          <span className="font-mono text-sm text-gray-700" data-id="element-7288" data-mp="1c485uiqm0raf">
                            {pseudonymFor(session.accountId)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-7289" data-mp="1lzrqg8zq43jv">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700" data-id="element-7290" data-mp="1r0yv3z6kkmmb">
                          {getModuleLabel(session.subjectId) || session.subjectId}
                        </span>
                      </td>
                      <td className="px-4 py-3" data-id="element-7291" data-mp="1zyrdjc7l8adx">
                        <div className="flex items-center gap-2" data-id="element-7292" data-mp="24svkhvgusif0">
                          <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-7293" data-mp="29qnldezh7evc">
                            <div className={`h-full rounded-full ${getMasteryBand(pct).barClass}`} style={{
                          width: `${pct}%`
                        }} data-id="element-7294" data-mp="2eq9zdqn4oz57" />
                          
                          </div>
                          <span className="text-xs font-medium text-gray-700 w-8" data-id="element-7295" data-mp="3gfge1ux5lbr6">
                            {pct}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap" data-id="element-7296" data-mp="3p7ibdkbxebx8">
                        <div className="flex flex-col" data-id="element-7297" data-mp="3l6ydufnfx6ak">
                          <span data-id="element-7298" data-mp="3l9klefabzlss">{formatDateWithTime(session.startTime)}</span>
                          <span className="text-xs text-gray-400" data-id="element-7299" data-mp="2xhvwza0mrhh4">
                            {formatRelativeTime(session.startTime, true)}
                          </span>
                        </div>
                      </td>
                    </tr>;
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <TablePagination page={page} pageSize={pageSize} total={filteredSessions.length} onPageChange={setPage} onPageSizeChange={size => {
          setPageSize(size);
          setPage(0);
        }} data-id="element-7300" data-mp="lrtc4xqbtz7g" />
        
      </div>
      </>}
    </PageLayout>;
}